// Approved relationship review: scoped local drafts, explicit save/return and retained history.
// These actions change the in-memory review, not CRM records or outreach permissions.
//
// W32 (2026-10-20) real-contact wiring: on a real HubSpot session
// (useReviewIdentity().isPreview === false) the screen renders the real
// identity and never the SARAH_RELATIONSHIPS / DUKE / MICHAEL fixture
// (preview only). "Add relationship" stays visible but inert ("Paused by
// the live-write gate").
//
// W35 (2026-10-20): the real session reads GET /v1/contacts/:id/
// relationships through the shared relative-fetch reader (the older
// runtime.signed read never fired in prod, MEASURED in the W30 audit row
// 9). W34 replaced the 503 with a 200 contact-property body:
//   { result:'OK', contactId, relationships:[], barnNameOwner:null,
//     relationshipStatus:null, source:'hubspot_contact_properties' }
// for Jodi, Karla and Tere (MEASURED by W34 against the local api-server;
// barn / vet_clinic / dvm objects hold 0 records portal-wide). Empty
// renders "No barn, trainer, vet or farrier on file yet"; barnNameOwner /
// relationshipStatus render when HubSpot has them.
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { WorkspaceChrome } from '../_shared/WorkspaceChrome.js';
import { PriorityFocus } from '../_shared/PriorityFocus.js';
import { IdentityRow } from '../_shared/IdentityRow.js';
import { CustomerTabs } from '../_shared/CustomerTabs.js';
import { FileShortcuts } from '../_shared/FileShortcuts.js';
import { UpNext } from '../_shared/UpNext.js';
import { ActionFooter } from '../_shared/ActionFooter.js';
import { useReviewIdentity } from '../_shared/useReviewIdentity.js';
import { RelationshipWorkspaceHeader } from './components/RelationshipWorkspaceHeader.js';
import { RelationshipSummary } from './components/RelationshipSummary.js';
import { RelationshipWorkspace } from './components/RelationshipWorkspace.js';
import { RelationshipHistory } from './components/RelationshipHistory.js';
import { RelationshipPrompt } from './components/RelationshipPrompt.js';
import { ConnectedHorse } from './components/ConnectedHorse.js';
import { OutsideSalesOpportunity } from './components/OutsideSalesOpportunity.js';
import { OwnershipReminder } from './components/OwnershipReminder.js';
import { RelationshipEditorDialog, EndOrReplaceDialog } from './components/RelationshipDialogs.js';
import { CEDAR_RIDGE_OPPORTUNITY, DUKE_HISTORY, DUKE_HISTORY_FOOTER, DUKE_PROMPT, OWNERSHIP_REMINDER,
  SARAH_HORSES, type ConnectedHorse as ConnectedHorseData, type Relationship } from './mockData.js';
import { getReviewReturnTarget, useReviewState } from '../_shared/ReviewState.js';
import { createHorseReviewState, HORSE_REVIEW_KEY, type HorseReviewState } from '../HorsesReview/reviewState.js';
import { createRelationshipReviewState, discardRelationshipDraft, discardRelationshipEndDraft, endDraftDirty, endRelationship,
  hasRelationshipDrafts, openRelationshipDraft, openRelationshipEndDraft, relationshipDraftDirty, RELATIONSHIP_REVIEW_KEY,
  saveRelationshipDraft, summariseRelationships, type EndOrReplaceValues, type RelationshipEditorValues, type RelationshipReviewState } from './reviewState.js';
import { SARAH } from '../../data/mockData.js';
import { useContactJsonRead, type ContactReadResult } from '../_shared/useSubscriptionSummary.js';
import './styles.css';
import './app.css';

// ---------------------------------------------------------------------------
// W35: GET /v1/contacts/:id/relationships (W34 contact-property sink)
// ---------------------------------------------------------------------------

export interface RelationshipsSummary {
  /** Store rows when the W2B RelationshipStore is composed; [] from the contact-property sink. */
  relationships: Record<string, unknown>[];
  /** HubSpot barn_name_owner ("Barn Name (Owns/Manages)"). */
  barnNameOwner: string | null;
  /** HubSpot relationship_status. */
  relationshipStatus: string | null;
  source: string | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

/** Pure projection of the route body; null when `relationships` is not an array. Exported for tests. */
export function projectRelationships(body: unknown): RelationshipsSummary | null {
  if (!isRecord(body) || !Array.isArray(body.relationships)) return null;
  return {
    relationships: body.relationships.filter(isRecord),
    barnNameOwner: str(body.barnNameOwner),
    relationshipStatus: str(body.relationshipStatus),
    source: str(body.source),
  };
}

export function useContactRelationships(customerId: string | null | undefined): ContactReadResult<RelationshipsSummary> {
  return useContactJsonRead<RelationshipsSummary>(customerId, 'relationships', projectRelationships);
}

/** Copy for the honest empty on a real contact. */
export const NO_RELATIONSHIPS_COPY = 'No barn, trainer, vet or farrier on file yet';

/** One line per store row from whichever identifying string fields it carries. */
export function describeRelationshipRow(row: Record<string, unknown>): string {
  const parts = ['type', 'role', 'organization', 'name', 'contact', 'status']
    .map((key) => str(row[key]))
    .filter((value): value is string => value !== null);
  return parts.length > 0 ? parts.join(' · ') : 'Relationship record';
}

export function RelationshipsReviewScreen(): ReactNode {
  const params = useParams();
  const customerId = params.customerId || SARAH.id;
  const identity = useReviewIdentity(customerId);
  const isPreview = identity.isPreview;
  const firstName = identity.firstName;

  const navigate = useNavigate(), location = useLocation();
  const [review, setReview] = useReviewState<RelationshipReviewState>(customerId, RELATIONSHIP_REVIEW_KEY, () => createRelationshipReviewState(customerId));
  const [horseReview] = useReviewState<HorseReviewState>(customerId, HORSE_REVIEW_KEY, () => createHorseReviewState(customerId));
  const horses = useMemo(() => {
    const entries = new Map<string, ConnectedHorseData>(SARAH_HORSES.map(horse => [horse.id, horse]));
    for (const horse of horseReview.horses) entries.set(horse.id, { id: horse.id, name: horse.name, breed: horse.breed,
      discipline: horse.discipline, summary: horse.reportedAge, barn: 'Not recorded', trainer: 'Not recorded', primaryVet: 'Not recorded' });
    return [...entries.values()];
  }, [horseReview.horses]);
  const selectedHorse = horses.find(horse => horse.id === review.selectedHorseId) ?? horses[0];
  const summary = summariseRelationships(review.relationships, selectedHorse.id, selectedHorse.name);
  const scopedRecords = review.relationships.filter(record => record.linkedHorseId === selectedHorse.id);
  const trainer = scopedRecords.find(record => record.status === 'current' && record.type === 'Trainer');
  const activeHorse = { ...selectedHorse, barn: summary.currentBarn.name, primaryVet: summary.primaryVet.name,
    trainer: trainer?.contact || trainer?.organization || 'Not recorded' };
  const changes = review.history.filter(event => event.horseId === selectedHorse.id || event.before?.linkedHorseId === selectedHorse.id);
  const prompt = { ...DUKE_PROMPT, question: `Is this still ${activeHorse.name}’s care team?`,
    context: 'Confirm the recorded connections with the customer. Keep former relationships and their effective dates.',
    currentCount: summary.currentCount, careRoles: new Set(scopedRecords.filter(record => record.status === 'current').map(record => record.type)).size, recentChanges: changes.length };
  const [editor, setEditor] = useState({ customerId, key: '', open: false });
  const [ending, setEnding] = useState({ customerId, targetId: '', open: false });
  const [feedback, setFeedback] = useState({ customerId, message: '', error: '', result: '' });
  const tell = (message: string, error = '', result = '') => setFeedback({ customerId, message, error, result });
  const editorDraft = review.drafts[editor.key], endDraft = review.endDrafts[ending.targetId];
  const endTarget = review.relationships.find(record => record.id === ending.targetId);
  const dirtyDrafts = Object.entries(review.drafts).filter(([, draft]) => relationshipDraftDirty(draft));
  const dirtyEndDrafts = Object.entries(review.endDrafts).filter(([, draft]) => endDraftDirty(draft));
  const dirty = hasRelationshipDrafts(review);
  const closeEditor = () => setEditor(value => ({ ...value, open: false }));
  const closeEnd = () => setEnding(value => ({ ...value, open: false }));

  const openEditor = (targetId: string | null, key = targetId ? `edit:${targetId}` : 'new') => {
    setReview(state => openRelationshipDraft(state, targetId, key));
    closeEnd();
    setEditor({ customerId, key, open: true });
    tell('');
  };
  const openAddDialog = () => openEditor(null);
  const openEditDialog = (record: Relationship) => openEditor(record.id);
  const openEndDialog = (record: Relationship) => {
    setReview(state => openRelationshipEndDraft(state, record.id));
    closeEditor();
    setEnding({ customerId, targetId: record.id, open: true });
    tell('');
  };
  const changeEditor = (patch: Partial<RelationshipEditorValues>) => {
    if (editor.customerId !== customerId) return;
    setReview(state => { const draft = state.drafts[editor.key];
      return draft ? { ...state, drafts: { ...state.drafts, [editor.key]: { ...draft, values: { ...draft.values, ...patch } } } } : state; });
    tell('');
  };
  const changeEnd = (patch: Partial<EndOrReplaceValues>) => {
    if (ending.customerId !== customerId) return;
    setReview(state => { const draft = state.endDrafts[ending.targetId];
      return draft ? { ...state, endDrafts: { ...state.endDrafts, [ending.targetId]: { ...draft, values: { ...draft.values, ...patch } } } } : state; });
    tell('');
  };
  const handleEditorSave = (returnAfter: boolean) => {
    if (editor.customerId !== customerId) return;
    try {
      const saved = saveRelationshipDraft(review, editor.key, horses.map(horse => horse.id));
      setReview(saved.state);
      setEditor(value => ({ ...value, key: saved.key }));
      const message = saved.changed ? 'Relationship saved in this preview · Jamie Dorsey' : 'No changes to save.';
      tell(message, '', message);
      if (returnAfter) {
        closeEditor();
        const target = getReviewReturnTarget(location.state, customerId);
        navigate(target.path, { state: target.state });
      }
    } catch (error) { tell('', error instanceof Error ? error.message : 'The connection could not be saved.'); }
  };
  const handleEndConfirm = () => {
    if (ending.customerId !== customerId) return;
    try {
      const saved = endRelationship(review, ending.targetId);
      setReview(saved.state);
      closeEnd();
      tell('Connection moved to Former in this preview. Previous details remain in history.');
      if (saved.replacementKey) setEditor({ customerId, key: saved.replacementKey, open: true });
    } catch (error) { tell('', error instanceof Error ? error.message : 'The end could not be recorded.'); }
  };
  const discardEditor = () => {
    setReview(state => discardRelationshipDraft(state, editor.key)); closeEditor(); tell('Unsaved relationship edits discarded. Saved history is retained.');
  };
  const discardEnd = () => {
    setReview(state => discardRelationshipEndDraft(state, ending.targetId)); closeEnd(); tell('Unsaved end details discarded. The saved connection is unchanged.');
  };

  // W35: the real relationships read. 'skipped' in preview / dev-mock
  // (same gate as the identity hook), so the fixture is never mixed with
  // a real read.
  const relationshipsRead = useContactRelationships(customerId);

  if (!isPreview) {
    // W32: real session. Inert add control, no fixture.
    // W35: real contact-property body with an honest empty.
    const relSummary = relationshipsRead.status === 'ready' ? relationshipsRead.data : null;
    const rows = relSummary?.relationships ?? [];
    const hasProps = !!relSummary && (relSummary.barnNameOwner !== null || relSummary.relationshipStatus !== null);
    const onFile = rows.length > 0 || hasProps;
    const whyNowBody =
      relationshipsRead.status === 'loading'
        ? 'Reading barn, trainer, veterinarian and farrier connections from HubSpot.'
        : relationshipsRead.status === 'error'
          ? `The relationships read failed (${relationshipsRead.errorCode ?? 'ERR'}).`
          : onFile
            ? 'HubSpot holds relationship details for this contact. Confirm them on the call before relying on them.'
            : 'No barn, trainer, veterinarian or farrier connection is on file for this contact in HubSpot.';
    return (
      <WorkspaceChrome screen="relationships" rail="my-work">
        <div className="breadcrumbs">
          <div>
            My work
            <span className="slash">/</span>
            <strong>{identity.customer.fullName}</strong>
          </div>
          {identity.statusPill ? <span className="preview">{identity.statusPill}</span> : null}
        </div>
        <PriorityFocus
          ariaLabel="RELATIONSHIPS · THE PEOPLE AROUND THEM"
          eyebrow="RELATIONSHIPS · THE PEOPLE AROUND THEM"
          heading={`Connect the people around ${firstName}`}
          what={{ label: 'WHAT', heading: `Know ${firstName}'s support team`, iconId: 'message' }}
          whyNow={{ label: 'WHY NOW', body: whyNowBody, iconId: 'clock' }}
          nextAction={{
            label: 'NEXT ACTION',
            body: 'Ask who helps care for the horse and note it in HubSpot; relationship records stay paused until the write gate opens.',
            detail: 'A relationship does not authorize outreach.',
            iconId: 'target',
          }}
          ownership={{ label: 'OWNERSHIP', iconId: 'people', detail: identity.ownerDetail }}
          owner={identity.owner}
        />
        <IdentityRow customer={identity.customer} badgeLabel={identity.badgeLabel} sinceLabel={identity.sinceLabel} />
        <FileShortcuts customerId={customerId} firstName={identity.customer.firstName} />
        <CustomerTabs customerId={customerId} active="relationships" />

        <div className="horse-toolbar" data-component="relationship-workspace-header">
          <div>
            <span className="eyebrow">{firstName.toUpperCase()}&rsquo;S CONNECTIONS</span>
            <h2>Good care is a team effort.</h2>
          </div>
          <button type="button" className="blue-action" disabled aria-describedby="relationship-gate-note">
            + Add relationship
          </button>
        </div>
        <section className="card" data-testid="relationships-real" style={{ padding: 24, margin: '12px 0' }}>
          <div className="section-heading">
            <h3>Care team on file</h3>
            <span id="relationship-gate-note">Paused by the live-write gate</span>
          </div>
          {relationshipsRead.status === 'loading' ? (
            <p role="status">Loading relationships…</p>
          ) : relationshipsRead.status === 'error' ? (
            <p role="alert" data-testid="relationships-real-error">
              Relationships could not load ({relationshipsRead.errorCode ?? 'ERR'}
              {relationshipsRead.httpStatus ? `, HTTP ${relationshipsRead.httpStatus}` : ''}).
              {relationshipsRead.errorDetail ? ` ${relationshipsRead.errorDetail}` : ''}
            </p>
          ) : !onFile ? (
            <div data-testid="relationships-real-empty">
              <p><strong>{NO_RELATIONSHIPS_COPY}</strong></p>
              <p>
                No barn, trainer, veterinarian or farrier connection is recorded for {firstName} in HubSpot.
                Relationship records provide context only and never authorize outreach.
              </p>
            </div>
          ) : (
            <div data-testid="relationships-real-on-file">
              {hasProps && relSummary ? (
                <dl data-testid="relationships-real-props" style={{ margin: '0 0 10px', fontSize: 12 }}>
                  {relSummary.barnNameOwner !== null ? (
                    <div>
                      <dt style={{ display: 'inline', fontWeight: 700 }}>Barn (owns or manages): </dt>
                      <dd style={{ display: 'inline', margin: 0 }}>{relSummary.barnNameOwner}</dd>
                    </div>
                  ) : null}
                  {relSummary.relationshipStatus !== null ? (
                    <div>
                      <dt style={{ display: 'inline', fontWeight: 700 }}>Relationship status: </dt>
                      <dd style={{ display: 'inline', margin: 0 }}>{relSummary.relationshipStatus}</dd>
                    </div>
                  ) : null}
                </dl>
              ) : null}
              {rows.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {rows.map((row, index) => (
                    <li key={str(row.id) ?? `relationship-${index}`} data-testid="relationships-real-row">{describeRelationshipRow(row)}</li>
                  ))}
                </ul>
              ) : null}
              <p style={{ marginTop: 8 }}>Relationship records provide context only and never authorize outreach.</p>
            </div>
          )}
        </section>

        <UpNext customerId={customerId} />
        <ActionFooter
          disabled={true}
          noteWhenDisabled="No relationships loaded for this contact"
          noteWhenEnabled="Ready to continue"
          summaryBody="Note any care-team detail in HubSpot before finishing."
        />
      </WorkspaceChrome>
    );
  }

  return (
    <WorkspaceChrome screen="relationships" rail="my-work">
      <div className="breadcrumbs">
        <div>
          My work
          <span className="slash">/</span>
          <strong>{SARAH.fullName}</strong>
        </div>
        <span className="preview">
          Design preview &middot; Illustrative relationship records
        </span>
      </div>
      <PriorityFocus
        ariaLabel="RELATIONSHIPS · THE PEOPLE AROUND THEM"
        eyebrow="RELATIONSHIPS · THE PEOPLE AROUND THEM"
        heading={`Connect the people around ${SARAH.firstName}`}
        what={{
          label: 'WHAT',
          heading: `Know ${SARAH.firstName}'s support team`,
          iconId: 'message',
        }}
        whyNow={{
          label: 'WHY NOW',
          body:
            'Barn, trainer and veterinarian details help the next conversation pick up where this one ends.',
          iconId: 'clock',
        }}
        nextAction={{
          label: 'NEXT ACTION',
          body: `Confirm who helps care for ${activeHorse.name} and how they are connected.`,
          detail: 'A relationship does not authorize outreach.',
          iconId: 'target',
        }}
        ownership={{
          label: 'OWNERSHIP',
          iconId: 'people',
          detail: 'Primary account owner. Coverage preserves ownership.',
        }}
      />
      <IdentityRow customer={SARAH} badgeLabel="Active subscriber" />
      <FileShortcuts customerId={customerId} firstName={SARAH.firstName} />
      <CustomerTabs customerId={customerId} active="relationships" />

      <RelationshipWorkspaceHeader
        customerFirstName={SARAH.firstName}
        onAddRelationship={openAddDialog}
      />

      <div className="relationship-review-controls">
        <label>Relationship horse<select value={selectedHorse.id}
          onChange={event => { setReview(state => ({ ...state, selectedHorseId: event.target.value })); closeEditor(); closeEnd(); tell(''); }}>
          {horses.map(horse => <option key={horse.id} value={horse.id}>{horse.name}</option>)}
        </select></label>
        {dirty && <span className="needs-pill">Unsaved relationship changes</span>}
      </div>
      {(dirtyDrafts.length > 0 || dirtyEndDrafts.length > 0) && <section className="relationship-draft-list" aria-label="Retained relationship drafts">
        {dirtyDrafts.map(([key, draft]) => <button type="button" className="outlined" key={key}
          onClick={() => openEditor(draft.targetId, key)}>Resume {draft.mode === 'add' ? 'new connection' : draft.values.organization || draft.values.contact || 'connection'} · {horses.find(horse => horse.id === draft.values.horseId)?.name || 'Horse'}</button>)}
        {dirtyEndDrafts.map(([id]) => <button type="button" className="outlined" key={id}
          onClick={() => { const record = review.relationships.find(item => item.id === id); if (record) openEndDialog(record); }}>
          Resume end · {review.relationships.find(record => record.id === id)?.organization || 'Connection'}</button>)}
      </section>}
      {feedback.customerId === customerId && feedback.message && <p className="relationship-review-result" role="status">{feedback.message}</p>}
      <RelationshipSummary summary={summary} portraitUrl={selectedHorse.id === 'horse_duke' ? undefined : ''} />

      <div className="relationship-layout">
        <RelationshipWorkspace
          relationships={scopedRecords}
          linkedHorseName={activeHorse.name}
          onEdit={openEditDialog}
          onEnd={openEndDialog}
          filter={review.filter}
          onFilterChange={filter => setReview(state => ({ ...state, filter }))}
        />
        <aside className="horse-side">
          <RelationshipPrompt
            prompt={prompt}
            onRecordConnection={openAddDialog}
          />
          <ConnectedHorse horse={activeHorse} customerId={customerId} />
          <OutsideSalesOpportunity opportunity={CEDAR_RIDGE_OPPORTUNITY} />
          <OwnershipReminder ownership={OWNERSHIP_REMINDER} />
        </aside>
      </div>

      <RelationshipHistory
        events={selectedHorse.id === 'horse_duke' ? DUKE_HISTORY : []}
        changes={changes}
        horseNames={Object.fromEntries(horses.map(horse => [horse.id, horse.name]))}
        retainedLabel={`${scopedRecords.length} relationship records retained`}
        explainer={DUKE_HISTORY_FOOTER.explainer}
        portraitUrl={selectedHorse.id === 'horse_duke' ? undefined : ''}
      />

      <UpNext customerId={customerId} />
      <ActionFooter
        disabled={dirty}
        noteWhenDisabled="Save or discard the unfinished relationship changes first"
        noteWhenEnabled="Relationship details reviewed · Ready to continue"
        summaryBody="Confirm the connection details or retain the next follow-up."
        onFinish={() => tell('Relationships reviewed in this preview. Live queue advancement is not connected.')}
      />

      {editorDraft && <RelationshipEditorDialog
        key={customerId + ':' + editor.key}
        open={editor.open && editor.customerId === customerId}
        draft={editorDraft}
        horses={horses}
        dirty={relationshipDraftDirty(editorDraft)}
        error={feedback.customerId === customerId ? feedback.error : ''}
        result={feedback.customerId === customerId ? feedback.result : ''}
        onClose={closeEditor}
        onSave={handleEditorSave}
        onChange={changeEditor}
        onDiscard={discardEditor}
      />}
      {endDraft && endTarget && <EndOrReplaceDialog
        key={customerId + ':' + ending.targetId}
        open={ending.open && ending.customerId === customerId}
        target={endTarget}
        draft={endDraft}
        linkedHorseName={horses.find(horse => horse.id === endTarget.linkedHorseId)?.name || 'Horse'}
        dirty={endDraftDirty(endDraft)}
        error={feedback.customerId === customerId ? feedback.error : ''}
        onClose={closeEnd}
        onConfirm={handleEndConfirm}
        onChange={changeEnd}
        onDiscard={discardEnd}
      />}
    </WorkspaceChrome>
  );
}

export default RelationshipsReviewScreen;
